<template>
  <BaseModal
    useForm
    :loading="loading"
    @submit="submit"
    @close="$emit('close')"
    :heading="$t('integrations.google_reserve.name')"
    small
  >
    <Flex
      direction="column"
      :gap="2"
    >
      <Flex
        :gap="0.5"
        align="center"
      >
        <div :class="$style.stepNumber">
          <BaseText
            color="inverted"
            bold
          >
            1
          </BaseText>
        </div>
        <BaseText bold>
          {{ $t('integrations.google_reserve.settings.step_1_title') }}
        </BaseText>
      </Flex>
      <Flex
        direction="column"
        :gap="1"
      >
        <BaseText>
          {{ $t('integrations.google_reserve.settings.step_1_description', brandName()) }}
        </BaseText>
        <BaseText href="https://www.google.com/business/">
          Google Business
        </BaseText>
      </Flex>

      <Flex
        :gap="0.5"
        align="center"
      >
        <div :class="$style.stepNumber">
          <BaseText
            color="inverted"
            bold
          >
            2
          </BaseText>
        </div>
        <BaseText bold>
          {{ $t('integrations.google_reserve.settings.step_2_title') }}
        </BaseText>
      </Flex>
      <Flex
        direction="column"
        :gap="1"
      >
        <BaseText>
          {{ $t('integrations.google_reserve.settings.step_2_description') }}
        </BaseText>
        <input
          ref="autocompleteInput"
          type="text"
          :class="$style.input"
          :placeholder="$t('integrations.google_reserve.search_for_business')"
        >
        <BaseCard v-if="place">
          <Flex
            direction="column"
            :gap="1"
          >
            <Flex
              :gap="0.5"
              align="center"
            >
              <div :class="$style.placeIcon">
                <BaseIcon
                  name="pin"
                  color="warning"
                />
              </div>
              <BaseText
                bold
                size="l"
              >
                {{ $t('integrations.google_reserve.settings.is_this_you') }}
              </BaseText>
            </Flex>
            <Flex
              v-for="detail in planeDetails"
              :key="detail.name"
              direction="column"
              :gap="0.5"
            >
              <BaseHeading
                v-if="detail.value || detail.value === 0"
                size="s"
              >
                {{ detail.label }}:
              </BaseHeading>
              <BaseText
                v-if="detail.value || detail.value === 0"
                oneLine
                :href="detail.href"
                v-test="`place-${detail.name}`"
              >
                {{ detail.value }}
              </BaseText>
            </Flex>
          </Flex>
        </BaseCard>
        <BaseCheckbox
          v-if="place"
          v-model="placeConfirmed"
          :label="$t('integrations.google_reserve.settings.confirm_place')"
          required
          v-test="'google-reserve-settings-confirm-place'"
        />
      </Flex>
    </Flex>
    <template #footer>
      <BaseButton
        color="inverted"
        @click="$emit('close')"
        v-test="'google-reserve-settings-cancel'"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        submitForm
        :disabled="!place"
        :loading="saving"
        v-test="'google-reserve-settings-save'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import config from '@/config';
import { brandName } from '@/helpers/brand-name';
import { handledException } from '@/helpers/datadog';
import { CREATE_GOOGLE_RESERVE_APP, GET_PLACE_INFO } from '../graphql';
import { loadGoogleMaps } from './google-maps-loader';

const emit = defineEmits(['close', 'onSaved']);

const GOOGLE_MAPS_API_KEY = config.googleMapsApiKey;
const autocompleteInput = ref(null);
const place = ref(null);
const placeConfirmed = ref(false);
let autocomplete = null;

const { t } = useI18n();

const planeDetails = computed(() => {
  return [
    {
      name: 'name',
      label: t('integrations.google_reserve.settings.business_name'),
      value: place.value?.name,
    },
    {
      name: 'website',
      label: 'Website',
      value: place.value?.website,
      href: place.value?.website,
    },
    {
      name: 'url',
      label: 'Google Maps',
      value: 'Google Maps',
      href: place.value?.url,
    },
    {
      name: 'rating',
      label: t('integrations.google_reserve.settings.rating'),
      value: place.value?.rating,
    },
  ];
});

const initAutocomplete = async () => {
  try {
    const google = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);

    if (google && autocompleteInput.value) {
      autocomplete = new google.maps.places.Autocomplete(
        autocompleteInput.value,
      );
      autocomplete.setFields([
        'place_id',
        'name',
        'website',
        'url',
        'rating',
        'types',
      ]);
      autocomplete.addListener('place_changed', onPlaceChanged);
    }
  } catch (error) {
    handledException(new Error('Google Maps API failed to load'), {
      error,
    });
  }
};

const onPlaceChanged = () => {
  const res = autocomplete.getPlace();

  place.value = {
    name: res.name,
    placeId: res.place_id,
    rating: res.rating,
    types: res.types,
    url: res.url,
    website: res.website,
  };

  placeConfirmed.value = false;
};

const { onResult, loading } = useQuery(GET_PLACE_INFO);

onResult(({ data }) => {
  place.value = data.placeInfo;
});

watch(
  loading,
  async (isLoading) => {
    if (!isLoading && !autocomplete) {
      await nextTick();
      initAutocomplete();
    }
  },
  { immediate: true },
);

const saving = ref(false);

const { mutate } = useMutation(CREATE_GOOGLE_RESERVE_APP);

const submit = () => {
  saving.value = true;

  mutate({
    input: {
      name: place.value.name,
      rating: String(place.value.rating),
      url: place.value.url,
      website: place.value.website,
      types: place.value.types,
      placeId: place.value.placeId,
    },
  }).then(() => {
    emit('onSaved');
    emit('close');
    saving.value = false;
  });
};
</script>

<style
  lang="scss"
  module
>
.input {
  @include input;
}

.placeIcon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-warning-lighter);
  border-radius: 8px;
}

.stepNumber {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-primary);
  border-radius: 100%;
}
</style>
